import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api/client';
import { PendingRequests } from '../api/pending-requests';
import type { FakesGroupedMapPage } from '../api/types';
import type { SerpMapSnapshotStatus } from '../../../api/src/fakes/serp-map-snapshot.types';
import { fakeError } from './fakes-presentation';

interface SnapshotState {
  key: string;
  data: FakesGroupedMapPage | null;
  status: SerpMapSnapshotStatus | null;
  error: string;
}

export function useSerpMapSnapshot(
  workspaceId: string,
  input: { top: number; runId?: string; changes?: 'changed' },
  version: string,
  active: boolean,
) {
  const { top, runId, changes } = input;
  const key = JSON.stringify([workspaceId, runId ?? null, top, changes ?? null]);
  const [state, setState] = useState<SnapshotState>({ key, data: null, status: null, error: '' });
  const current = useRef(state);
  // Effect restarts (volume refresh, StrictMode, reopening) share in-flight
  // requests within this mounted map. Never cache responses across users.
  const pending = useRef(new PendingRequests());
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!active) return;
    let live = true,
      running = false;
    let timer: ReturnType<typeof setTimeout>;
    const update = (patch: Partial<SnapshotState>) => {
      if (!live) return;
      const previous =
        current.current.key === key
          ? current.current
          : { key, data: null, status: null, error: '' };
      current.current = { ...previous, ...patch };
      setState(current.current);
    };
    const selection = { top, runId, ...(changes ? { changes } : {}) };
    const load = async () => {
      const response = await pending.current.run(`${key}:body`, () =>
        api.fakesGroupedMap(workspaceId, selection),
      );
      if ('pending' in response) update({ status: response.snapshotStatus, error: '' });
      else update({ data: response, status: response.snapshotStatus ?? null, error: '' });
    };
    const poll = async (initial = false) => {
      if (!live || running) return;
      clearTimeout(timer);
      running = true;
      try {
        if (initial) await load();
        else {
          const status = await pending.current.run(`${key}:status`, () =>
            api.fakesGroupedMapStatus(workspaceId, selection),
          );
          if (!live) return;
          const previous =
            current.current.key === key ? current.current.data?.materialization : null;
          update({ status, error: '' });
          if (
            status.snapshot &&
            (status.snapshot.revision !== previous?.revision ||
              status.snapshot.builtAt !== previous?.builtAt)
          )
            await load();
        }
      } catch (error) {
        const denied = error instanceof ApiError && [401, 403, 404].includes(error.status);
        update({ error: fakeError(error), ...(denied ? { data: null, status: null } : {}) });
      } finally {
        running = false;
        if (live)
          timer = setTimeout(() => void poll(), current.current.status?.outdated ? 5_000 : 30_000);
      }
    };
    const focus = () => {
      if (!document.hidden) void poll();
    };
    // Reopening or a page refresh signal only needs the lightweight status when
    // this selection is already visible. Download a body if its version changed.
    void poll(current.current.key !== key || !current.current.data);
    window.addEventListener('focus', focus);
    return () => {
      live = false;
      clearTimeout(timer);
      window.removeEventListener('focus', focus);
    };
  }, [workspaceId, key, runId, top, changes, version, attempt, active]);
  const visible = state.key === key ? state : { key, data: null, status: null, error: '' };
  return { ...visible, retry: () => setAttempt((value) => value + 1) };
}
